import React from "react";
import { PROJECTION_TEXTS, projectionHover } from "../StationPerformance.consts";
import { berlinYearMonth, projectToMonthEnd } from "../utils/projection";

export interface ProjectedValueProps {
  value: number | null | undefined;
  yearMonth: string;
  closure: "OPEN" | "GRACE" | "SEALED" | null | undefined;
  liveDayIncluded: boolean;
  /** e.g. euros from cents, kWh, integer sessions */
  format: (v: number) => string;
  /** Injectable for tests. */
  now?: Date;
  /** Last month's actual value of the same figure; when present and non-zero, an arrow and % change are shown. */
  previousValue?: number | null;
  /** Last month's name for the hover note, e.g. "September 2026". */
  previousLabel?: string;
}

/** Same colours as the Clearing Overview projection. */
const UP_COLOR = "#16a34a";
const DOWN_COLOR = "#ef4444";

/**
 * % change of the projected month-end value vs last month; null when there is nothing to compare with.
 * Divides by |previous| so the sign always follows the direction of the value: −100 → −50 is a rise (+50%),
 * which matters for figures that can be negative (the cocharge THG share when incoming < outgoing).
 */
export const projectedChangePercent = (projected: number, previous: number | null | undefined): number | null => {
  if (previous === null || previous === undefined || Number.isNaN(previous) || previous === 0) return null;
  return ((projected - previous) / Math.abs(previous)) * 100;
};

const MONTH_ABBREVIATIONS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const formatMonthEnd = (monthEnd: Date): string =>
  `${monthEnd.getUTCDate()} ${MONTH_ABBREVIATIONS[monthEnd.getUTCMonth()]}`;

/** Month-end projection line for an additive figure of the OPEN month; nothing otherwise. */
const ProjectedValue: React.FC<ProjectedValueProps> = ({
  value,
  yearMonth,
  closure,
  liveDayIncluded,
  format,
  now,
  previousValue,
  previousLabel,
}) => {
  if (closure !== "OPEN") return null;
  if (value === null || value === undefined || Number.isNaN(value)) return null;

  const at = now ?? new Date();
  if (yearMonth !== berlinYearMonth(at)) return null;

  const projection = projectToMonthEnd(value, at, yearMonth, liveDayIncluded);
  if (!projection) {
    return <div className="text-sm text-color-secondary">{PROJECTION_TEXTS.noData}</div>;
  }

  const change = projectedChangePercent(projection.value, previousValue);
  const comparedWith =
    change === null ? "" : ` · vs ${previousLabel ?? "last month"} (${format(previousValue as number)})`;
  const hover = `${projectionHover(projection, liveDayIncluded)}${comparedWith}`;
  // Rounded to the displayed precision, so a change shown as "0.0%" never carries an arrow or a colour.
  const shown = change === null ? null : Math.round(change * 10) / 10;
  const isFlat = shown === 0;
  const isUp = (shown ?? 0) > 0;

  return (
    <div className="text-sm text-color-secondary" title={hover}>
      {PROJECTION_TEXTS.projected(format(projection.value), formatMonthEnd(projection.monthEnd))}
      {change !== null && (
        <span
          className="ml-1 font-semibold"
          style={isFlat ? undefined : { color: isUp ? UP_COLOR : DOWN_COLOR }}
          data-testid="projection-change"
        >
          {isFlat ? "0.0%" : `${isUp ? "↑" : "↓"} ${Math.abs(change).toFixed(1)}%`}
        </span>
      )}
    </div>
  );
};

export default ProjectedValue;
